import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, Phone, Clock, Loader2 } from "lucide-react";

export default function ContactUs() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Contact Us | Cynos";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      setStatus("Please complete all fields before submitting.");
      return;
    }
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 900));
    setLoading(false);
    setStatus("Your message has been sent. We usually respond within 24 hours.");
    setName("");
    setEmail("");
    setPhone("");
    setSubject("");
    setMessage("");
  };

  return (
    <main className="min-h-screen bg-black px-4 py-24 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-6xl space-y-12">
        <section className="rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-xl shadow-black/20">
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">Contact Us</p>
              <h1 className="mt-4 text-4xl font-anton uppercase text-white">Need help? We’re here for you.</h1>
              <p className="mt-4 max-w-2xl text-sm text-zinc-400">Reach out for support, order questions, returns guidance, or any product help. Our team responds quickly on weekdays.</p>
            </div>
            <div className="space-y-4 rounded-3xl border border-white/10 bg-black/40 p-6">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Email</p>
                <p className="text-white">support@cynos.in</p>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">WhatsApp</p>
                <p className="text-white">+91 98765 43210</p>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Support Hours</p>
                <p className="text-white">Monday–Saturday, 10 AM – 7 PM</p>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="rounded-3xl border border-white/10 bg-cynos-card p-8 shadow-xl shadow-black/20">
            <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">Send a Request</p>
            <h2 className="mt-4 text-3xl font-anton uppercase text-white">Get in touch</h2>
            <p className="mt-3 text-sm text-zinc-400">Submit the form below and our support team will reach out shortly.</p>
            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <input value={name} onChange={(e) => setName(e.target.value)} className="rounded-2xl border border-white/10 bg-black/60 px-4 py-3 text-white outline-none focus:border-cynos-red" placeholder="Full Name" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-2xl border border-white/10 bg-black/60 px-4 py-3 text-white outline-none focus:border-cynos-red" placeholder="Email" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-2xl border border-white/10 bg-black/60 px-4 py-3 text-white outline-none focus:border-cynos-red" placeholder="Phone" />
                <input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-2xl border border-white/10 bg-black/60 px-4 py-3 text-white outline-none focus:border-cynos-red" placeholder="Subject" />
              </div>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} className="w-full rounded-3xl border border-white/10 bg-black/60 px-4 py-3 text-white outline-none focus:border-cynos-red" placeholder="Message" />
              {status && <div className="rounded-2xl bg-emerald-500/15 px-4 py-3 text-sm text-emerald-200">{status}</div>}
              <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-cynos-red px-4 py-3 text-sm uppercase tracking-[0.2em] text-black transition hover:bg-cynos-red/90 disabled:opacity-70">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
                {loading ? "Sending" : "Submit Message"}
              </button>
            </form>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="rounded-3xl border border-white/10 bg-black/40 p-8">
            <p className="text-xs uppercase tracking-[0.3em] text-cynos-red">Quick Links</p>
            <div className="mt-6 space-y-4 text-sm text-zinc-300">
              <Link to="/shipping-returns" className="block rounded-2xl border border-white/10 bg-black/70 px-4 py-3 transition hover:border-cynos-red">Shipping & Returns</Link>
              <Link to="/track-order" className="block rounded-2xl border border-white/10 bg-black/70 px-4 py-3 transition hover:border-cynos-red">Track Order</Link>
              <Link to="/size-guide" className="block rounded-2xl border border-white/10 bg-black/70 px-4 py-3 transition hover:border-cynos-red">Size Guide</Link>
            </div>
            <p className="mt-8 text-xs uppercase tracking-[0.25em] text-zinc-500">Reply time</p>
            <p className="mt-3 text-sm text-zinc-400">We usually respond within 24 hours.</p>
          </motion.div>
        </section>
      </motion.div>
    </main>
  );
}
